import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { Modal } from "react-bootstrap";
import type { Ad } from "@models/Ad.model";
import type { AdGroupListItem, AdStatus } from "@models/adGroup.model";
import { Brand } from "@models/brand.model";
import { AudienceDefinitionService } from "@services/engagements/launch/audienceDefinitionService";
import { LaunchService } from "@services/launches/LaunchService";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { NotificationService } from "@services/notifications/NotificationService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { engagementStore } from "src/modules/engagement/engagementStore";
import Loader from "src/components/common/loader/loader";
import AdPicker from "@components/AdPicker/AdPicker";
import AdEditorModal from "src/modules/createEditor/AdEditorModal";
import AdRow from "./AdRow";
import RenameActionModal from "src/modules/contentCatalog/components/RenameActionModal";

type Props = {
  adGroup: AdGroupListItem;
  /** The campaign's sub-organization; null when it has none. */
  organizationId: number | null;
  onBack: () => void;
  onEdit: () => void;
  onChanged: () => void;
};

type GroupAd = {
  launch_ad_id: number;
  launch_ad_status: AdStatus;
  ad: Ad;
  source_template_name: string | null;
  append_utm: boolean;
  destination_url: string | null;
};

const RAIL_STAGES: { key: AdStatus | "ALL"; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "TODO", label: "To do" },
  { key: "IN_PROGRESS", label: "In progress" },
  { key: "IN_REVIEW", label: "In review" },
  { key: "CHANGES_REQUESTED", label: "Changes requested" },
  { key: "APPROVED", label: "Approved" },
  // Last, and not part of "All" (MPN-1612). A rejected GuidePost is meant to stop
  // competing for attention; the chip is how Ops gets at it deliberately.
  { key: "DO_NOT_USE", label: "Do not use" },
];

export default function AdGroupDetail({ adGroup, organizationId, onBack, onEdit, onChanged }: Props) {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const engagementId = selectedEngagement.id;

  const [ads, setAds] = useState<GroupAd[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [filter, setFilter] = useState<AdStatus | "ALL">("ALL");
  const [showAssignAdModal, setShowAssignAdModal] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [pendingUnlink, setPendingUnlink] = useState<GroupAd | null>(null);
  // Renaming is offered here as well as on the Content page (MPN-1508). Both list the same
  // campaign ads, so an action available on one and missing on the other reads as a bug.
  const [retitling, setRetitling] = useState<Ad | null>(null);
  const [retitleBusy, setRetitleBusy] = useState(false);
  const [brand, setBrand] = useState<Brand>(new Brand());

  const [searchParams, setSearchParams] = useSearchParams();
  const adParam = searchParams.get("ad");
  const openAdId = adParam && /^\d+$/.test(adParam) ? Number(adParam) : null;

  const loadAds = async () => {
    const launchAds = await new AudienceDefinitionService().listAds(
      adGroup.launch_id,
      adGroup.id,
      "ad",
    );
    setAds(
      launchAds
        .filter((la) => !!la.ad)
        .map((la) => ({
          launch_ad_id: la.id,
          launch_ad_status: la.status as AdStatus,
          ad: la.ad!,
          source_template_name: la.source_template_name,
          append_utm: la.append_utm,
          destination_url: la.destination_url,
        })),
    );
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    loadAds()
      .catch(() => {
        if (cancelled) return;
        setError("Could not load this ad group's ads.");
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
     
  }, [adGroup.id, adGroup.launch_id]);

  useEffect(() => {
    const orgId = String(selectedEngagement.client?.id ?? "");
    if (!orgId || orgId === "0") return;
    let cancelled = false;
    new OrganizationService()
      .getOrganizationBrand(orgId)
      .then((result) => {
        if (cancelled || !result) return;
        setBrand(result);
      })
      .catch(() => {
        // Brand fetch is non-critical.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEngagement.client?.id]);

  const counts = useMemo(() => {
    const byStatus: Record<AdStatus, number> = {
      TODO: 0,
      IN_PROGRESS: 0,
      IN_REVIEW: 0,
      CHANGES_REQUESTED: 0,
      APPROVED: 0,
      DO_NOT_USE: 0,
    };
    for (const a of ads) byStatus[a.launch_ad_status]++;
    return byStatus;
  }, [ads]);

  const inProgressCount = counts.IN_PROGRESS;

  // "All" means every stage a GuidePost is still live in, which excludes Do Not Use
  // (MPN-1612). Selecting the chip is the toggle that reveals them -- they are hidden by
  // default, never removed, so Ops can still find and reverse one.
  const visibleAds = ads.filter((a) => a.launch_ad_status !== "DO_NOT_USE");
  const filteredAds =
    filter === "ALL" ? visibleAds : ads.filter((a) => a.launch_ad_status === filter);

  // The only place that writes ad status. The dropdown calls this directly;
  // the submit paths below call it too, then notify — never the reverse.
  // Returns whether the status actually changed — see ContentStep.setStatus (MPN-2061).
  const setStatus = async (launchAdId: number, next: AdStatus): Promise<boolean> => {
    setBusyId(launchAdId);
    setError(null);
    const previous = ads;
    setAds((prev) =>
      prev.map((a) => (a.launch_ad_id === launchAdId ? { ...a, launch_ad_status: next } : a)),
    );
    try {
      await new LaunchService().updateLaunchAdStatus(launchAdId, next);
      onChanged();
      return true;
    } catch (e: any) {
      setAds(previous);
      // The server's own words, not a generic retry prompt. A GuidePost refused for
      // having no destination URL (MPN-2061) will never succeed on a retry, and
      // "Try again" sent Ops round a loop with nothing telling them what to fix.
      setError(e?.message ?? "Could not change that ad's status. Try again.");
      return false;
    } finally {
      setBusyId(null);
    }
  };

  /*
      The UTM toggle (MPN-1536).

      This page renders the same AdRow as the campaign Content view, and Rachel was
      looking at THIS one when she reported the toggle missing — the first pass only
      wired the other caller. Optimistic like setStatus beside it, rolled back the same
      way, and it takes the server's re-resolved URL rather than guessing at it.
  */
  const setUtm = async (launchAdId: number, next: boolean) => {
    setBusyId(launchAdId);
    setError(null);
    const previous = ads;
    setAds((prev) =>
      prev.map((a) => (a.launch_ad_id === launchAdId ? { ...a, append_utm: next } : a)),
    );
    try {
      const updated = await new LaunchService().setLaunchAdUtm(launchAdId, next);
      setAds((prev) =>
        prev.map((a) =>
          a.launch_ad_id === launchAdId
            ? { ...a, append_utm: updated.append_utm, destination_url: updated.destination_url }
            : a,
        ),
      );
    } catch {
      setAds(previous);
      setError("Could not change that ad's URL tagging. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const notifyClient = () =>
    new NotificationService().send_notification("CONTENT_REVIEW_REQUEST", {
      engagement: { id: engagementId },
    });

  const handleSubmitOne = async (launchAdId: number) => {
    if (!(await setStatus(launchAdId, "IN_REVIEW"))) return; // no notification inside
    await notifyClient();
    setShowSubmitted(true);
  };

  const handleSubmitAll = async () => {
    const pending = ads.filter((a) => a.launch_ad_status === "IN_PROGRESS");
    const refused: string[] = [];
    for (const ad of pending) {
      if (!(await setStatus(ad.launch_ad_id, "IN_REVIEW"))) refused.push(ad.ad.name);
    }
    // setStatus clears the banner on each pass, so only the last failure would have
    // survived the loop. Name them all, and do not tell the client to come and look
    // unless something actually went.
    if (refused.length) {
      setError(`Not submitted: ${refused.join(", ")}. See the ad for what is missing.`);
    }
    if (refused.length === pending.length) return;
    await notifyClient(); // once, after all of them
    setShowSubmitted(true);
  };

  const handleUnlink = async () => {
    if (!pendingUnlink) return;
    const { launch_ad_id, ad } = pendingUnlink;
    setBusyId(launch_ad_id);
    setError(null);
    try {
      // The unlink archives the ad in this ad group. It used to be followed by a
      // content_removed event that archived it in every ad group using it (MPN-1909).
      await new AudienceDefinitionService().unLinkAds(adGroup.launch_id, adGroup.id, ad.id);
      setPendingUnlink(null);
      await loadAds();
      onChanged();
    } catch {
      setError("Could not unlink that ad. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRetitle = async (nextName: string) => {
    if (!retitling) return;
    setRetitleBusy(true);
    setError(null);
    try {
      await new ContentCatalogService().retitleAd(retitling.id, nextName);
      setRetitling(null);
      await loadAds();
      onChanged();
    } catch {
      setError("Could not rename that ad. Try again.");
    } finally {
      setRetitleBusy(false);
    }
  };

  const openEditor = (adId: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("ad", String(adId));
      return next;
    });
  };

  const closeEditor = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("ad");
        return next;
      },
      { replace: true },
    );
  };

  const onSaveAdPicker = async (selectedAdIds: number[]) => {
    const catalogService = new ContentCatalogService();
    const launchService = new LaunchService();
    const audienceService = new AudienceDefinitionService();

    const duplicatedAdIds = await Promise.all(
      selectedAdIds.map(async (adId) => {
        const duplicated = await catalogService.duplicateAd(adId, { is_template: false });
        return duplicated.id as number;
      }),
    );

    await launchService.addLaunchAds(adGroup.launch_id, duplicatedAdIds);
    await audienceService.linkAds(adGroup.launch_id, adGroup.id, duplicatedAdIds);
    await loadAds();
    onChanged();
  };

  if (isLoading) return <Loader />;

  return (
    <div className="adgroup-pane">
      <div className="adgroup-detail__head">
        <button type="button" className="btn btn-sm btn-link px-0" onClick={onBack}>
          ‹ Ad Groups
        </button>
        <div className="adgroup-detail__title-row">
          <div>
            <h5 className="adgroup-detail__title">{adGroup.name}</h5>
            <div className="adgroup-detail__meta">{adGroup.launch_name}</div>
          </div>
          <div className="adgroup-detail__head-actions">
            {inProgressCount > 1 && (
              <button type="button" className="btn btn-sm btn-primary" onClick={handleSubmitAll}>
                Submit all for review ({inProgressCount})
              </button>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onEdit}>
              Edit
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowAssignAdModal(true)}
            >
              + Assign ad
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="stage-rail">
        {RAIL_STAGES.map((stage) => (
          <button
            key={stage.key}
            type="button"
            className={`stage-rail__item${filter === stage.key ? " on" : ""}`}
            onClick={() => setFilter(stage.key)}
          >
            {stage.label}
            <span className="stage-rail__count">
              {stage.key === "ALL" ? visibleAds.length : counts[stage.key]}
            </span>
          </button>
        ))}
      </div>

      {filteredAds.length === 0 ? (
        <div className="adgroup-section__empty">No ads in this stage</div>
      ) : (
        <div className="ad-grid">
          {filteredAds.map((a) => (
            <AdRow
              key={a.launch_ad_id}
              ad={a.ad}
              launchAdId={a.launch_ad_id}
              status={a.launch_ad_status}
              isBusy={busyId === a.launch_ad_id}
              onOpen={() => openEditor(a.ad.id)}
              onStatusChange={(next) => setStatus(a.launch_ad_id, next)}
              onSubmitForReview={() => handleSubmitOne(a.launch_ad_id)}
              onRetitle={() => setRetitling(a.ad)}
              onUnlink={() => setPendingUnlink(a)}
              sourceTemplateName={a.source_template_name}
              appendUtm={a.append_utm}
              destinationUrl={a.destination_url}
              onToggleUtm={(next) => setUtm(a.launch_ad_id, next)}
            />
          ))}
        </div>
      )}

      <AdPicker
        isOpen={showAssignAdModal}
        onClose={() => setShowAssignAdModal(false)}
        serviceProviderOrganizationId={selectedEngagement.service_provider.id}
        clientOrganizationId={selectedEngagement.client.id}
        onSave={(ids) => void onSaveAdPicker(ids)}
      />

      <AdEditorModal
        adId={openAdId ?? -1}
        brand={brand}
        modalOpen={openAdId != null}
        setModalOpen={(isOpen) => {
          if (!isOpen) closeEditor();
        }}
        launchId={adGroup.launch_id}
        organizationId={organizationId ?? selectedEngagement.client?.id}
        adGroupId={adGroup.id}
      />

      <RenameActionModal
        show={retitling !== null}
        title="Rename for this campaign"
        initialValue={retitling?.name ?? ""}
        confirmLabel="Rename"
        isLoading={retitleBusy}
        onConfirm={(next) => void handleRetitle(next)}
        onClose={() => setRetitling(null)}
      />

      <Modal show={!!pendingUnlink} onHide={() => setPendingUnlink(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="div" className="fw-semibold">Unlink ad</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small">
            <span className="fw-semibold">{pendingUnlink?.ad.name || "This ad"}</span> will be
            removed from {adGroup.name}. The ad itself stays in the catalog.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setPendingUnlink(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={busyId != null}
            onClick={() => void handleUnlink()}
          >
            Unlink
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSubmitted} onHide={() => setShowSubmitted(false)} centered>
        <Modal.Body className="text-center py-4">
          <div className="fw-semibold mb-2">Request sent</div>
          <div className="small text-muted">Your request for review has been sent.</div>
          <button
            type="button"
            className="btn btn-sm btn-primary mt-3"
            onClick={() => setShowSubmitted(false)}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>
    </div>
  );
}
